import React, {
  forwardRef,
  Ref,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Backdrop, Box, Button, Fade, Modal as MuiModal } from "@mui/material";
import { IModal, IModalPosition } from "./IModal";
import "./Modal.scss";
import MaterialIcons from "../../../assests/MaterialIcons/MaterialIcons";

const Modal = forwardRef(function Modal(
  {
    title,
    open,
    onClose,
    children,
    className,
    containerClassName,
    style,
    width,
    isOnboardingModal = false,
    buttons,
    buttonPosition = "end",
    verticalAlign = "center",
    showCloseIcon,
    height,
    maxHeight,
    disableEnforceFocus = true,
    disableAutoFocus,
  }: IModal,
  ref: Ref<HTMLDivElement>,
) {
  const modalRef = useRef<HTMLDivElement>(null);
  const [modalStyle, setModalStyle] = useState<IModalPosition>({
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
  });

  const showButtons = useMemo(
    () => (buttons ?? [])?.filter((btn) => btn?.hide !== true),
    [buttons],
  );

  useEffect(() => {
    const timeOut = setTimeout(() => {
      if (open && modalRef?.current) {
        if (verticalAlign === "top") {
          setModalStyle({
            top: "5%",
            left: "50%",
            transform: "translate(-50%, 0%)",
          });
        } else if (verticalAlign === "center") {
          setModalStyle({
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
          });
        } else if (verticalAlign === "bottom") {
          setModalStyle({
            bottom: "5%",
            left: "50%",
            transform: "translate(-50%, 0%)",
          });
        }
      }
    }, 100);

    return () => {
      clearTimeout(timeOut);
    };
  }, [open, verticalAlign]);

  return (
    <MuiModal
      open={open}
      ref={ref}
      onClose={() => onClose()}
      closeAfterTransition
      disableEnforceFocus={disableEnforceFocus}
      disableAutoFocus={disableAutoFocus}
      slots={{ backdrop: Backdrop }}
      slotProps={{
        backdrop: {
          timeout: 500,
        },
      }}
    >
      <Fade in={open} className={isOnboardingModal ? "modal-onboarding" : ""}>
        <Box
          ref={modalRef}
          className={containerClassName}
          sx={{
            ...modalStyle,
            border: "none",
            outline: "none",
            boxShadow: "0 1px 5px rgba(0, 0, 0, 0.25)",
            position: "absolute",
            width: width ?? "auto",
            maxWidth: "90%",
            maxHeight: "90vh",
            bgcolor: isOnboardingModal
              ? "var(--onboarding-bg-color)"
              : "var(--background-color)",
            borderRadius: "20px",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Header */}
          <div className="modal-header">
            <label className="modal-label ellipsis">{title ?? ""}</label>
            <span className="close" title="close" onClick={() => onClose()}>
              <MaterialIcons.Close />
            </span>
          </div>

          {/* Body */}
          <div
            className={`modal-body${className ? ` ${className}` : ""}`}
            style={{
              ...style,
              overflowY: "auto", // enforce scroll
              flex: 1, // take remaining space
            }}
          >
            {children}

            {showButtons && showButtons?.length > 0 && (
              <div
                className={`flex item-center justify-${buttonPosition ?? "end"} gap-3`}
              >
                {showButtons?.map((button, buttonIndex) => (
                  <Button
                    key={buttonIndex}
                    disabled={button?.disabled}
                    variant={
                      isOnboardingModal
                        ? "text"
                        : (button?.buttonVariant ?? "text")
                    }
                    onClick={() => button?.onClick && button?.onClick()}
                    tabIndex={button?.tabIndex}
                    autoFocus={button?.autoFocus ?? true}
                  >
                    {button?.title ?? ""}
                  </Button>
                ))}
              </div>
            )}
          </div>
        </Box>
      </Fade>
    </MuiModal>
  );
});

export default Modal;
